import React from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";

import RequireAuth from "./Layout/AccountLayout/RequireAuth.js";
import RequireRole from "./Layout/AccountLayout/RequireRole.js";
import RouteError from "./Components/ErrorBoundary/RouteError.js";
import Error404Page from "./Pages/Error404Page/Error404Page.js";

// Layout

const AccountLayout = React.lazy(() =>
  import("./Layout/AccountLayout/AccountLayout.js")
);

// Superadmin routes

const superadminRoutes = {
  path: "superadmin",
  children: [
    {
      index: true,
      element: <Navigate to="/superadmin/respondentsRegistry" replace />,
    },
    {
      path: "respondentsRegistry",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/RespondentsListPage/RespondentsListPage.js")
      ),
    },
    {
      path: "respondentProfile/:id",
      Component: React.lazy(() =>
        import(
          "./Pages/Superadmin/RespondentProfilePage/RespondentProfilePage.js"
        )
      ),
    },
    {
      path: "usersList",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/UsersListPage/UsersListPage.js")
      ),
    },
    {
      path: "respondentReports/:respondentId",
      Component: React.lazy(() =>
        import(
          "./Pages/Superadmin/RespondentReportsPage/RespondentReportsPage.js"
        )
      ),
    },
    {
      path: "reportProcess/:respondentId/:id",
      Component: React.lazy(() =>
        import("./Pages/Participant/SubmissionPage/SubmissionPage.js")
      ),
    },
    {
      path: "reportReview/:respondentId/:id",
      Component: React.lazy(() =>
        import("./Pages/Participant/ReviewPage/ReviewPage.js")
      ),
    },
    {
      path: "income/stats/:respondentId/:id",
      Component: React.lazy(() =>
        import(
          "./Pages/Participant/IncomeReports/SubmissionStatsPage/SubmissionStatsPage.js"
        )
      ),
    },
    {
      path: "income/compare/:respondentId/:id",
      Component: React.lazy(() =>
        import(
          "./Pages/Participant/IncomeReports/RegionComparisonPage/RegionComparisonPage.js"
        )
      ),
    },
    {
      path: "newRespondent",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/NewRespondentPage/NewRespondentPage.js")
      ),
    },
    {
      path: "importQueue",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/ImportQueueListPage/ImportQueueListPage.js")
      ),
    },
    {
      path: "reportsList",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/ReportsListPage/ReportsListPage.js")
      ),
    },
    {
      path: "templatesList",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/TemplatesListPage/TemplatesListPage.js")
      ),
    },
    {
      path: "reportSendersList",
      Component: React.lazy(() =>
        import(
          "./Pages/Superadmin/ReportSendersListPage/ReportSendersListPage.js"
        )
      ),
    },
    {
      path: "reestrNpo",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/ReestrNpoPage/ReestrNpoListPage.js")
      ),
    },
    {
      path: "reestrNpo/:id",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/ReestrNpoPage/ReestrNpoEditPage.js")
      ),
    },
    {
      path: "support/tickets",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/Support/TicketsListPage.js")
      ),
    },
    {
      path: "support/tickets/:id",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/Support/TicketDetailPage.js")
      ),
    },
    {
      path: "errorLog",
      Component: React.lazy(() =>
        import("./Pages/Superadmin/ErrorLog/ErrorLogPage.js")
      ),
    },
    {
      path: "reports",
      children: [
        {
          path: "info/:id",
          Component: React.lazy(() =>
            import("./Pages/Admin/ReportInfoPage/ReportInfoPage.js")
          ),
        },
        {
          path: "tree/:sender_id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/ReportAggregationTree/ReportAggregationTree.js"
            )
          ),
        },
        {
          path: "templateEditor/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/ReportTemplateEditorPage/ReportTemplateEditorPage.js"
            )
          ),
        },
        {
          path: "templateJSONEditor/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/ReportTemplateJSONPage/ReportTemplateJSONPage2.js"
            )
          ),
        },
        {
          path: "templateImport/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/reportTemplateImportPage/reportTemplateImportPage.js"
            )
          ),
        },
      ],
    },
  ],
};

// Admin routes

const adminRoutes = {
  path: "admin",
  children: [
    {
      index: true,
      element: <Navigate to="/admin/reports/registry" replace />,
    },
    {
      path: "dashboard",
      element: <Navigate to="/" replace />,
    },
    {
      path: "reports",
      children: [
        {
          path: "registry",
          Component: React.lazy(() =>
            import("./Pages/Admin/ReportsListPage/ReportsListPage2.js")
          ),
        },
        {
          path: "info/:id",
          Component: React.lazy(() =>
            import("./Pages/Admin/ReportInfoPage/ReportInfoPage.js")
          ),
        },
        {
          path: "tree/:sender_id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/ReportAggregationTree/ReportAggregationTree.js"
            )
          ),
        },
        {
          path: "templateEditor/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/ReportTemplateEditorPage/ReportTemplateEditorPage.js"
            )
          ),
        },
        {
          path: "templateJSONEditor/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/ReportTemplateJSONPage/ReportTemplateJSONPage2.js"
            )
          ),
        },
        {
          path: "templateImport/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Admin/reportTemplateImportPage/reportTemplateImportPage.js"
            )
          ),
        },
      ],
    },
    {
      path: "classifiers",
      children: [
        {
          path: "registry",
          Component: React.lazy(() =>
            import("./Pages/Admin/ClassifiersListPage/ClassifiersListPage.js")
          ),
        },
        {
          path: "editor/:id",
          Component: React.lazy(() =>
            import("./Pages/Admin/ClassifierEditorPage/ClassifierEditorPage.js")
          ),
        },
      ],
    },
    {
      path: "support/tickets",
      Component: React.lazy(() =>
        import("./Pages/Support/AdminTicketsListPage.js")
      ),
    },
    {
      path: "support/tickets/:id",
      Component: React.lazy(() =>
        import("./Pages/Support/AdminTicketDetailPage.js")
      ),
    },
  ],
};

// Legal entity owner routes

const legalEntityOwnerRoutes = {
  path: "legalEntityOwner",
  children: [
    {
      index: true,
      element: <Navigate to="/legalEntityOwner/sign" replace />,
    },
    {
      path: "users",
      Component: React.lazy(() =>
        import("./Pages/LegalEntityOwner/UsersPage/UsersPage.js")
      ),
    },
    {
      path: "sign",
      Component: React.lazy(() =>
        import("./Pages/LegalEntityOwner/ReportSignPage/ReportSignPage.js")
      ),
    },
    {
      path: "support/tickets",
      Component: React.lazy(() =>
        import("./Pages/Support/LeoTicketsListPage.js")
      ),
    },
    {
      path: "support/tickets/:id",
      Component: React.lazy(() =>
        import("./Pages/Support/LeoTicketDetailPage.js")
      ),
    },
  ],
};

// Respondent routes

const respondentRoutes = {
  path: "participant",
  children: [
    {
      index: true,
      element: <Navigate to="/participant/outcome/required" replace />,
    },
    {
      path: "profile",
      Component: React.lazy(() =>
        import("./Pages/Participant/ProfilePage/ProfilePage.js")
      ),
    },
    {
      path: "reportReview/:id",
      Component: React.lazy(() =>
        import("./Pages/Participant/ReviewPage/ReviewPage.js")
      ),
    },
    {
      path: "reportProcess/:id",
      Component: React.lazy(() =>
        import("./Pages/Participant/SubmissionPage/SubmissionPage.js")
      ),
    },
    {
      path: "npo",
      Component: React.lazy(() => import("./Pages/NPO/MainPage/MainPage.js")),
    },
    {
      path: "npo-projects",
      Component: React.lazy(() =>
        import("./Pages/NPO/ProjectsPage/ProjectsPage.js")
      ),
    },
    {
      path: "integration-reports",
      Component: React.lazy(() => import("./Pages/Participant/IntegrationReports/IntegrationReports.jsx")),
    },
    {
      path: "income",
      children: [
        {
          path: "list",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/IncomeReports/IncomeReportsPage/IncomeReportsPage.js"
            )
          ),
        },
        {
          path: "tree/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/IncomeReports/TreeView/TreeView.jsx"
            )
          ),
        },
        {
          path: "stats/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/IncomeReports/SubmissionStatsPage/SubmissionStatsPage.js"
            )
          ),
        },
        {
          path: "compare/:id",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/IncomeReports/RegionComparisonPage/RegionComparisonPage.js"
            )
          ),
        },
        {
          path: "history",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/IncomeReports/HistoryPage/HistoryPage.js"
            )
          ),
        },
      ],
    },
    {
      path: "outcome",
      children: [
        {
          path: "required",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/OutcomeReports/OutcomeReportsPage/OutcomeReportsPage.js"
            )
          ),
        },
        {
          path: "drafts",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/OutcomeReports/DraftsPage/DraftsPage.js"
            )
          ),
        },
        {
          path: "history",
          Component: React.lazy(() =>
            import(
              "./Pages/Participant/OutcomeReports/HistoryPage/HistoryPage.js"
            )
          ),
        },
      ],
    },
    {
      path: "support/tickets",
      Component: React.lazy(() =>
        import("./Pages/Support/ParticipantTicketsListPage.js")
      ),
    },
    {
      path: "support/tickets/:id",
      Component: React.lazy(() =>
        import("./Pages/Support/ParticipantTicketDetailPage.js")
      ),
    },
  ],
};

// Support routes (public)

const supportRoutes = {
  path: "support",
  children: [
    {
      path: "contacts",
      element: <Navigate to="/support/guides" replace />,
    },
    {
      path: "guides",
      Component: React.lazy(() =>
        import("./Pages/Support/GuidesPage/GuidesPage.js")
      ),
    },
    {
      path: "qa",
      Component: React.lazy(() => import("./Pages/Support/QAPage/QAPage.js")),
    },
  ],
};

const integrationRoutes = {
  path: "/integration",
  children: [
    {
      index: true,
      Component: React.lazy(() =>
        import("./Pages/Integration/IntegrationList.jsx")
      ),
    }]

}

// Monitor routes (read-only)

const monitorRoutes = {
  path: "monitor",
  element: <RequireRole roles={["monitor"]} />,
  children: [
    {
      index: true,
      element: <Navigate to="/monitor/forms" replace />,
    },
    {
      path: "forms",
      Component: React.lazy(() => import("./Pages/Monitor/FormsPage.js")),
    },
    {
      path: "forms/:aggregationId",
      Component: React.lazy(() => import("./Pages/Monitor/SendersPage.js")),
    },
    {
      path: "reportReview/:id",
      Component: React.lazy(() =>
        import("./Pages/Participant/ReviewPage/ReviewPage.js")
      ),
    },
    {
      path: "draft/:id",
      Component: React.lazy(() =>
        import("./Pages/Participant/ReviewPage/ReviewPage.js")
      ),
    },
    {
      path: "compare/:id",
      Component: React.lazy(() =>
        import(
          "./Pages/Participant/IncomeReports/RegionComparisonPage/RegionComparisonPage.js"
        )
      ),
    },
    {
      path: "support/tickets",
      Component: React.lazy(() =>
        import("./Pages/Support/ParticipantTicketsListPage.js")
      ),
    },
    {
      path: "support/tickets/:id",
      Component: React.lazy(() =>
        import("./Pages/Support/ParticipantTicketDetailPage.js")
      ),
    },
  ],
};

const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <RouteError />,
    element: <AccountLayout />,
    children: [
      {
        // page crashes render inside the layout, not over it
        errorElement: <RouteError inline />,
        children: [
          {
            index: true,
            Component: React.lazy(() => import("./Pages/HomePage/HomePage.js")),
          },
          {
            path: "npo",
            Component: React.lazy(() => import("./Pages/NPO/MainPage/MainPage.js")),
          },
          {
            path: "npo-projects",
            Component: React.lazy(() =>
              import("./Pages/NPO/ProjectsPage/ProjectsPage.js")
            ),
          },
          {
            path: "about",
            element: <Navigate to="/" replace />,
          },
          {
            path: "stats-by-ministry",
            element: <Navigate to="/" replace />,
          },
          {
            path: "login",
            Component: React.lazy(() => import("./Pages/LoginPage/LoginPage.js")),
          },
          supportRoutes,
          {
            element: <RequireAuth />,
            children: [
              superadminRoutes,
              adminRoutes,
              legalEntityOwnerRoutes,
              respondentRoutes,
              integrationRoutes,
              monitorRoutes,
            ],
          },
          { path: "*", element: <Error404Page /> },
        ],
      },
    ],
  },
]);

function AppRouter() {
  return <RouterProvider router={router} />;
}

export default AppRouter;
